// Derivar a otra financiera desde Forma de pago o Score: llama al silo y lleva al cobro de los
// extras (venta activa) o a la Bienvenida (venta en pausa), como decide `referralNextStep`.
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { referralFailure, referralNextStep } from '../externalReferral';
import type { SessionRefreshContext } from '../promotionSlots';
import type { FinancierOption } from '../types';
import { useExternalReferral, useWizardSession } from './useWizard';

/** Lo que recibe la pieza de Promociones tras derivar una venta con extras. */
export interface ReferralSlotContext extends SessionRefreshContext {
    sessionId: string;
    externalProvider: string;
}

/** Hook de Promociones: reevalúa y suelta las promos que ya no aplican (su lógica, no la nuestra). */
export type AfterReferral = (ctx: ReferralSlotContext) => void | Promise<void>;

export function useReferralFlow(sessionId: string, afterReferral?: AfterReferral) {
    const navigate = useNavigate();
    const { refresh } = useWizardSession(sessionId);
    const externalReferral = useExternalReferral(sessionId);

    /** `true` si la venta se derivó; con error deja el aviso y devuelve `false`. */
    const refer = async (provider: FinancierOption): Promise<boolean> => {
        let next;
        try {
            next = await externalReferral.mutateAsync(provider.code);
        } catch (e) {
            const failure = referralFailure(e);
            if (failure.reload) void refresh();
            toast.error(failure.message);
            return false;
        }
        const step = referralNextStep(next);
        if (step === 'payment') {
            if (afterReferral) {
                try {
                    await afterReferral({
                        sessionId,
                        externalProvider: provider.code,
                        refreshSession: async () => {
                            await refresh();
                        },
                    });
                } catch {
                    // Promociones tiene su red de seguridad en el consumidor; el cobro sigue.
                }
                void refresh();
            }
            toast.info(`La venta pasó a ${provider.name}. Cobra los extras de contado.`);
        } else {
            toast.info('Venta en pausa — se retoma en la Bienvenida cuando el cliente regrese.');
        }
        await navigate({ to: '/wizard/s/$sessionId/$step', params: { sessionId, step } });
        return true;
    };

    return { refer, isPending: externalReferral.isPending };
}
